import { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, IconButton, Tooltip, TablePagination,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { equipmentService } from '@smartgrow/shared';
import type { EquipmentInventory, CreateEquipmentRequest, EquipmentCondition, EquipmentStatus } from '@smartgrow/shared';

const CONDITIONS: EquipmentCondition[] = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'];
const STATUSES: EquipmentStatus[] = ['AVAILABLE', 'IN_USE', 'UNDER_MAINTENANCE', 'DECOMMISSIONED'];
const EQUIPMENT_TYPES = [
  'TRACTOR', 'PLOUGH', 'HARROW', 'SEEDER', 'SPRAYER', 'IRRIGATION_PUMP',
  'WATER_HOSE', 'CULTIVATOR', 'HARVESTER', 'THRESHER', 'GRAIN_DRYER',
  'STORAGE_BIN', 'HOE', 'RAKE', 'SHOVEL', 'WHEELBARROW', 'MACHETE',
  'PRUNING_SHEARS', 'FERTILIZER_SPREADER', 'PESTICIDE_SPRAYER',
  'SOIL_TESTER', 'GENERATOR', 'FUEL_TANK', 'SAFETY_GEAR', 'OTHER',
];

const statusColor = (s: string): 'success' | 'primary' | 'warning' | 'default' => {
  switch (s) {
    case 'AVAILABLE': return 'success';
    case 'IN_USE': return 'primary';
    case 'UNDER_MAINTENANCE': return 'warning';
    default: return 'default';
  }
};

const conditionColor = (c: string): 'success' | 'primary' | 'warning' | 'error' | 'default' => {
  switch (c) {
    case 'EXCELLENT': return 'success';
    case 'GOOD': return 'primary';
    case 'FAIR': return 'warning';
    case 'POOR':
    case 'DAMAGED': return 'error';
    default: return 'default';
  }
};

const emptyForm = (): CreateEquipmentRequest => ({
  name: '',
  equipmentType: 'TRACTOR',
  condition: 'GOOD',
  status: 'AVAILABLE',
  serialNumber: '',
  location: '',
  supplierName: '',
  notes: '',
});

export const EquipmentTab = () => {
  const qc = useQueryClient();

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateEquipmentRequest>(emptyForm());
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['equipment', statusFilter, page, debouncedSearch],
    queryFn: () => equipmentService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
    }),
  });

  const equipment: EquipmentInventory[] = data?.content ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['equipment'] });

  const saveMutation = useMutation({
    mutationFn: () => editingId ? equipmentService.update(editingId, form) : equipmentService.create(form),
    onSuccess: () => { invalidate(); setFormOpen(false); setEditingId(null); setForm(emptyForm()); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Save failed'),
  });

  const openEdit = (item: EquipmentInventory) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      equipmentType: item.equipmentType,
      serialNumber: item.serialNumber ?? '',
      condition: item.condition,
      status: item.status,
      purchaseCost: item.purchaseCost,
      currentValue: item.currentValue,
      location: item.location ?? '',
      supplierName: item.supplierName ?? '',
      lastMaintenanceDate: item.lastMaintenanceDate,
      nextMaintenanceDate: item.nextMaintenanceDate,
      notes: item.notes ?? '',
      quantityReceived: item.quantityReceived,
      quantityAvailable: item.quantityAvailable,
    });
    setFormOpen(true);
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search equipment..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} />
        <TextField select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="ALL">All Statuses</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingId(null); setForm(emptyForm()); setFormOpen(true); }}>
          Add Equipment
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Qty Received</TableCell>
                <TableCell>Qty Available</TableCell>
                <TableCell>Serial No.</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Next Maintenance</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipment.map((item: EquipmentInventory, i: number) => (
                <TableRow key={item.id} hover>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="600">
                      {item.name}
                      {item.maintenanceDueSoon && ' ⚠'}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{item.equipmentTypeLabel || item.equipmentType}</Typography></TableCell>
                  <TableCell>{item.quantityReceived ?? '—'}</TableCell>
                  <TableCell>{item.quantityAvailable ?? '—'}</TableCell>
                  <TableCell>{item.serialNumber ?? '—'}</TableCell>
                  <TableCell><Chip label={item.statusLabel || item.status} color={statusColor(item.status)} size="small" /></TableCell>
                  <TableCell><Chip label={item.conditionLabel || item.condition} color={conditionColor(item.condition)} size="small" variant="outlined" /></TableCell>
                  <TableCell>{item.location ?? '—'}</TableCell>
                  <TableCell>
                    {item.nextMaintenanceDate
                      ? <Typography variant="body2" color={item.maintenanceDueSoon ? 'warning.main' : 'inherit'}>
                          {new Date(item.nextMaintenanceDate).toLocaleDateString()}
                        </Typography>
                      : '—'}
                  </TableCell>
                  <TableCell>{item.currentValue != null ? `₦${item.currentValue.toLocaleString()}` : '—'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(item)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {equipment.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography color="text.secondary" py={3}>No equipment found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" />
          <TextField select label="Equipment Type *" value={form.equipmentType} onChange={(e) => setForm({ ...form, equipmentType: e.target.value })} fullWidth size="small">
            {EQUIPMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Qty Received" type="number" value={form.quantityReceived ?? ''} onChange={(e) => setForm({ ...form, quantityReceived: parseInt(e.target.value) || undefined })} fullWidth size="small" inputProps={{ min: 0 }} />
          <TextField label="Qty Available" type="number" value={form.quantityAvailable ?? ''} onChange={(e) => setForm({ ...form, quantityAvailable: parseInt(e.target.value) || undefined })} fullWidth size="small" inputProps={{ min: 0 }} />
          <TextField select label="Condition" value={form.condition ?? 'GOOD'} onChange={(e) => setForm({ ...form, condition: e.target.value })} fullWidth size="small">
            {CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={form.status ?? 'AVAILABLE'} onChange={(e) => setForm({ ...form, status: e.target.value })} fullWidth size="small">
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Serial Number" value={form.serialNumber ?? ''} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} fullWidth size="small" />
          <TextField label="Purchase Cost (₦)" type="number" value={form.purchaseCost ?? ''} onChange={(e) => setForm({ ...form, purchaseCost: parseFloat(e.target.value) || undefined })} fullWidth size="small" />
          <TextField label="Current Value (₦)" type="number" value={form.currentValue ?? ''} onChange={(e) => setForm({ ...form, currentValue: parseFloat(e.target.value) || undefined })} fullWidth size="small" />
          <TextField label="Location" value={form.location ?? ''} onChange={(e) => setForm({ ...form, location: e.target.value })} fullWidth size="small" />
          <TextField label="Supplier Name" value={form.supplierName ?? ''} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} fullWidth size="small" />
          <TextField label="Last Maintenance (YYYY-MM-DD)" type="date" value={form.lastMaintenanceDate ?? ''} onChange={(e) => setForm({ ...form, lastMaintenanceDate: e.target.value || undefined })} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="Next Maintenance (YYYY-MM-DD)" type="date" value={form.nextMaintenanceDate ?? ''} onChange={(e) => setForm({ ...form, nextMaintenanceDate: e.target.value || undefined })} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="Notes" value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth size="small" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name.trim()}>
            {editingId ? 'Save Changes' : 'Add Equipment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
